import type { TimetableItemProps } from '@versita/frontend/campus-ui-web';
import type { TimetableEvent } from '@versita/shared/models';
import { useHandleQueryError } from '@versita/frontend/api-utils';
import {
  assignCategoryColor,
  extractTimetableItemsCategories,
} from '@versita/shared/timetable-utils';
import { refreshCategories } from '@versita/frontend/timetable-state';
import {
  pipe,
  applySpec,
  propOr,
  prop,
  groupBy,
  mapObjIndexed,
  map,
  join,
  identity,
} from 'ramda';
import { useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { DateTime } from 'luxon';

const NO_DATA: never[] = []; // stable default value

export const MY_TIMETABLE_ID = 'ME' as const;

/** simplified version of {@link UseQueryHookResult} */
interface TimetableQueryResult {
  data?: TimetableEvent[];
  error?: unknown;
  isFetching: boolean;
  isLoading: boolean;
}
/**
 * Designed to be composed with a useQuery hook whose output matches these args
 * @sideEffect refreshes the categories in the redux store if needed
 */
export const useTimetableItems = pipe(
  useHandleQueryError,
  useRefreshCategoriesIfChanged,
  applySpec<{
    timetableItems: Record<string, TimetableItemProps[]>;
    isFetchingEvents: boolean;
    isLoadingEvents: boolean;
    isError: boolean;
    refetch: () => void;
  }>({
    timetableItems: pipe(propOr([], 'data'), parseEvents),
    isFetchingEvents: prop('isFetching'),
    isLoadingEvents: prop('isLoading'),
    isError: prop('isError'),
    refetch: prop('refetch'),
  }),
);

function parseEvents(events: TimetableEvent[]) {
  return pipe(groupByWeekDay, mapToViewModel)(events);
}
const groupByWeekDay = groupBy<TimetableEvent>((event) =>
  DateTime.fromISO(event.startTime).weekday.toString(),
) as (events: TimetableEvent[]) => Record<string, TimetableEvent[]>;
// const addSpacesBeforeUppercaseLetters = replace(/(?<=[a-z])([A-Z])/g, ' $1');
const mapToViewModel = mapObjIndexed(
  map<TimetableEvent, /* to */ TimetableItemProps>(
    applySpec({
      id: prop('id'),
      start: prop('startTime'),
      end: prop('finishTime'),
      tag: prop('type'),
      label: prop('name'),
      color: pipe(prop('eventTypeId'), assignCategoryColor),
      footer: pipe(prop('roomNames'), join(',\n')),
      eventDetails: identity,
    }),
  ),
);

function useRefreshCategoriesIfChanged(result: TimetableQueryResult) {
  const { data: timetableEvents = NO_DATA } = result;
  const dispatch = useDispatch();
  const categories = useMemo(
    // this will cause infinite re-renders if we don't memoize it as technically
    // it changes every time useTimetableItems is run (which is every render).
    // This would then trigger the useEffect below which would cause yet
    // another render, and so on.
    () => extractTimetableItemsCategories(timetableEvents),
    [timetableEvents],
  );
  useEffect(() => {
    // refresh the categories in the redux store if needed
    // (since we only grab the available categories from the currently-loaded
    // events so when they change we may have new ones to add)
    dispatch(refreshCategories(categories));
  }, [categories, dispatch]);
  return result;
}
